import { useTranslation } from 'react-i18next';
import {
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { HistoryRegular } from '@fluentui/react-icons';
import {
  useClientObligationEvents,
  useClientStateTaxObligations,
} from '../../services/stateTaxClientsApi';
import {
  formatObligationEventTime,
  formatDeltaValue,
} from '../../utils/filingActivityHelpers';
import type { ObligationEvent, StateTaxObligationSummary } from '../../types';

/**
 * Per-client cross-obligation lifecycle feed (deferred from F2). Companion to
 * ObligationHistoryPopover which shows one obligation at a time. Same event
 * shape; merges persisted events across all of the client's obligations with
 * one synthesized "created" row per obligation that has Dataverse
 * EffectiveStartDate. Tags each event with the obligation it belongs to so
 * staff can scan the audit trail across the whole client without drilling
 * into each badge.
 */
const ACTIVITY_LIMIT = 25;

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: `${tokens.spacingVerticalS} 0`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:first-child': {
      borderTop: 'none',
      paddingTop: 0,
    },
  },
  rowHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  eventTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  obligationTag: {
    marginLeft: tokens.spacingHorizontalS,
  },
  time: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
  },
  delta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  reason: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

function obligationLabel(o: StateTaxObligationSummary | undefined): string | null {
  if (!o) return null;
  const parts = [o.taxType, o.stateLabel].filter((p): p is string => !!p && p.trim().length > 0);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function ObligationEventsCard({ stateTaxClientId }: { stateTaxClientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();

  const events = useClientObligationEvents(stateTaxClientId);
  const obligations = useClientStateTaxObligations(stateTaxClientId);

  const obligationsById = new Map<string, StateTaxObligationSummary>();
  for (const o of obligations.data ?? []) {
    obligationsById.set(o.id, o);
    // STX-AUDIT-01: events from the Cosmos obligation edit (e.g. form_changed) are keyed by the id FILINGS carry, which for a seeded twin
    // differs from the Dataverse row's own id (72 of 80 production obligations). Index both, so those events still name their obligation.
    if (o.filingObligationId && !obligationsById.has(o.filingObligationId)) obligationsById.set(o.filingObligationId, o);
  }

  const all = events.data ?? [];
  const visible = all.slice(0, ACTIVITY_LIMIT);
  const overflow = Math.max(0, all.length - visible.length);

  const isLoading = events.isLoading;
  const isError = events.isError;

  return (
    <div className="card">
      <div className={styles.header}>
        <Text className={styles.heading}>
          <HistoryRegular style={{ marginRight: 6, verticalAlign: '-3px' }} />
          {t('clientDetailPage.obligationEvents.title')}
        </Text>
      </div>

      <Text className={styles.subtitle} block>
        {t('clientDetailPage.obligationEvents.subtitle')}
      </Text>

      {isLoading && (
        <div style={{ padding: '12px 0' }}>
          <Spinner size="tiny" label={t('clientDetailPage.obligationEvents.loading')} />
        </div>
      )}

      {isError && (
        <MessageBar intent="error" style={{ marginTop: 12 }}>
          <MessageBarBody>{t('clientDetailPage.obligationEvents.loadError')}</MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && visible.length === 0 && (
        <Text className={styles.emptyHint} style={{ display: 'block', padding: '12px 0' }}>
          {t('clientDetailPage.obligationEvents.empty')}
        </Text>
      )}

      {visible.length > 0 && (
        <div className={styles.list}>
          {visible.map((ev: ObligationEvent) => {
            const beforeKeys = Object.keys(ev.before ?? {});
            const afterKeys = Object.keys(ev.after ?? {});
            const deltaKeys = Array.from(new Set([...beforeKeys, ...afterKeys]));
            const attribution =
              ev.changedByName?.trim()
              || ev.changedById?.trim()
              || t('clientDetailPage.obligationEvents.unknownActor');
            const obligationTag = obligationLabel(obligationsById.get(ev.obligationId));

            return (
              <div key={ev.id} className={styles.row}>
                <div className={styles.rowHeader}>
                  <div>
                    <Text className={styles.eventTitle}>
                      {t(`clientDetailPage.obligationHistory.eventType.${ev.eventType}`, {
                        defaultValue: ev.eventType,
                      })}
                    </Text>
                    {obligationTag && (
                      <Badge appearance="outline" size="small" className={styles.obligationTag}>
                        {obligationTag}
                      </Badge>
                    )}
                  </div>
                  <Text className={styles.time}>
                    {formatObligationEventTime(ev.changedAt)}
                  </Text>
                </div>
                <Text className={styles.delta}>
                  {t('clientDetailPage.obligationHistory.byActor', { name: attribution })}
                </Text>
                {/* Binary active-flip events (deactivated / reactivated / removed) skip
                    the delta rows — the label already carries the whole meaning. */}
                {deltaKeys.length > 0
                  && ev.eventType !== 'deactivated'
                  && ev.eventType !== 'reactivated'
                  && ev.eventType !== 'removed' && (
                  <div>
                    {deltaKeys.map((key) => {
                      const before = (ev.before ?? {})[key];
                      const after = (ev.after ?? {})[key];
                      return (
                        <Text key={key} className={styles.delta} block>
                          {ev.eventType === 'created'
                            ? `${key}: ${formatDeltaValue(after)}`
                            : t('clientDetailPage.obligationHistory.fromTo', {
                                field: key,
                                before: formatDeltaValue(before),
                                after: formatDeltaValue(after),
                              })}
                        </Text>
                      );
                    })}
                  </div>
                )}
                {ev.periodLabel?.trim() && (
                  <Text className={styles.delta} block>
                    {t('clientDetailPage.obligationHistory.forPeriod', { period: ev.periodLabel.trim() })}
                  </Text>
                )}
                {ev.reason?.trim() && (
                  <Text className={styles.reason}>
                    {t('clientDetailPage.obligationHistory.reason', { reason: ev.reason.trim() })}
                  </Text>
                )}
              </div>
            );
          })}
        </div>
      )}

      {overflow > 0 && (
        <Text
          className={styles.subtitle}
          style={{ display: 'block', marginTop: 8, fontStyle: 'italic' }}
        >
          {t('common.moreCount', { count: overflow })}
        </Text>
      )}
    </div>
  );
}
